import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, track } from "@/lib/api";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { useSectionObserver } from "@/hooks/useSectionObserver";
import { NexusBackdrop } from "@/components/site/NexusBackdrop";
import { SiteHeader } from "@/components/site/SiteHeader";
import { HeroSection } from "@/components/site/HeroSection";
import { ProductsSection } from "@/components/site/ProductsSection";
import { AboutSection } from "@/components/site/AboutSection";
import { CareersSection } from "@/components/site/CareersSection";
import { ContactSection } from "@/components/site/ContactSection";
import { SiteFooter } from "@/components/site/SiteFooter";

const Loading = ({ isError, retry }) => (
  <div className="grid min-h-[100svh] place-items-center px-6 text-center" role="status" data-testid="content-loading">
    {isError ? (
      <p className="text-nx-muted">
        The page content could not load.{" "}
        <button type="button" onClick={retry} className="text-link" data-testid="content-retry-button">Try again</button>
      </p>
    ) : (
      <span className="sr-only">Loading</span>
    )}
  </div>
);

export default function SitePage() {
  const reduced = usePrefersReducedMotion();
  const [interest, setInterest] = useState("BuiltOS");
  const { data, isError, refetch } = useQuery({ queryKey: ["content"], queryFn: () => api.get("/content").then((r) => r.data) });
  const s = data?.sections;
  useSectionObserver(Boolean(s));

  useEffect(() => {
    if (s && window.location.hash) document.querySelector(window.location.hash)?.scrollIntoView();
  }, [s]);

  const onCta = useCallback((cta, nextInterest) => {
    track("cta_click", { cta });
    if (nextInterest) setInterest(nextInterest);
  }, []);

  return (
    <div className="site-root">
      <a href="#main" className="skip-link" data-testid="skip-link">Skip to content</a>
      <NexusBackdrop reduced={reduced} />
      <SiteHeader onCta={onCta} />
      <main id="main" tabIndex={-1} className="relative z-10 outline-none">
        {s ? (
          <>
            <HeroSection c={s.hero} />
            <ProductsSection c={s.products} products={data.products} onCta={onCta} />
            <AboutSection c={s.about} />
            <CareersSection c={s.careers} onCta={onCta} />
            <ContactSection c={s.contact} interest={interest} setInterest={setInterest} products={data.products} />
          </>
        ) : (
          <Loading isError={isError} retry={refetch} />
        )}
      </main>
      {s && <SiteFooter products={data.products} />}
    </div>
  );
}
